import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useMutation, useQuery } from "@tanstack/react-query";
import { memo, useContext, useEffect, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../team";

interface KlaviyoMetric {
  name: string;
  lastFired: string | null;
}

export const KlaviyoMetricsHealthCheckCard = memo(
  function KlaviyoMetricsHealthCheckCard() {
    const team = useContext(TeamContext);
    const rpcClient = useContext(RedoAdminRpcClientContext);
    const [isPolling, setIsPolling] = useState(false);
    const [hasShownSuccessToast, setHasShownSuccessToast] = useState(false);
    const [currentWorkflowId, setCurrentWorkflowId] = useState<string | null>(
      null,
    );

    // Query to load cached results on mount (startHealthCheck=false)
    const metricsQuery = useQuery({
      queryKey: ["klaviyoMetricsHealthCheck", team?._id, currentWorkflowId],
      queryFn: async () => {
        if (!team || !rpcClient) {
          throw new Error("Team or RPC client not available");
        }
        return rpcClient.klaviyoMetricsHealthCheck({
          teamId: team._id,
          startHealthCheck: false,
          workflowId: currentWorkflowId || undefined,
        });
      },
      enabled: !!team && !!rpcClient,
      refetchOnWindowFocus: false,
      refetchInterval: isPolling ? 2000 : false, // Poll every 2 seconds when checking
    });

    const metrics: KlaviyoMetric[] = metricsQuery.data?.metrics || [];

    // Handle polling completion
    useEffect(() => {
      if (isPolling && metricsQuery.data?.scanned && !hasShownSuccessToast) {
        setIsPolling(false);
        setHasShownSuccessToast(true);
        const metricsCount = metrics.length;
        toast(
          `Health check completed. Found ${metricsCount} Malomo metric${metricsCount !== 1 ? "s" : ""}.`,
          { variant: "success" },
        );
      }
    }, [isPolling, metricsQuery.data, hasShownSuccessToast, metrics.length]);

    const runHealthCheckMutation = useMutation({
      mutationFn: async () => {
        if (!team || !rpcClient) {
          throw new Error("Team or RPC client not available");
        }
        return rpcClient.klaviyoMetricsHealthCheck({
          teamId: team._id,
          startHealthCheck: true,
        });
      },
      onSuccess: (response) => {
        // Store the workflow ID so polling checks the correct workflow
        if (response.workflowId) {
          setCurrentWorkflowId(response.workflowId);
        }

        // Workflow should always start fresh (never return cached results when startHealthCheck is true)
        if (!response?.scanned) {
          setIsPolling(true);
          setHasShownSuccessToast(false);
          // Trigger immediate refetch with the new workflow ID
          void metricsQuery.refetch();
        }
      },
      onError: (error) => {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        toast(`Failed to run health check: ${errorMessage}`, {
          variant: "error",
        });
        setIsPolling(false);
      },
    });

    const formatTimestamp = (timestamp: string | null): string => {
      if (!timestamp) return "Never";
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60_000);
      const diffHours = Math.floor(diffMs / 3_600_000);
      const diffDays = Math.floor(diffMs / 86_400_000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60)
        return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
      if (diffHours < 24)
        return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
      return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    };

    return (
      <Card title="Malomo Metrics Health Check">
        <Flex dir="column" gap="lg">
          <Text fontSize="sm">
            Monitor the health of Malomo tracking metrics in Klaviyo. This
            checks when each Malomo metric was last fired to help identify if
            the integration is working.
          </Text>

          {metrics.length > 0 && (
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr
                    style={{
                      backgroundColor: "#f9fafb",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontSize: "14px",
                        fontWeight: 600,
                      }}
                    >
                      Metric Name
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontSize: "14px",
                        fontWeight: 600,
                      }}
                    >
                      Last Fired
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.map((metric) => (
                    <tr
                      key={metric.name}
                      style={{ borderBottom: "1px solid #e5e7eb" }}
                    >
                      <td style={{ padding: "12px", fontSize: "14px" }}>
                        {metric.name}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          fontSize: "14px",
                          color: metric.lastFired ? "#374151" : "#9ca3af",
                        }}
                      >
                        {formatTimestamp(metric.lastFired)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <RedoButton
            disabled={
              runHealthCheckMutation.isPending ||
              isPolling ||
              !team ||
              !rpcClient
            }
            hierarchy="primary"
            onClick={() => runHealthCheckMutation.mutate()}
            size="sm"
            text={
              runHealthCheckMutation.isPending || isPolling
                ? "Checking..."
                : "Run Health Check"
            }
          />
        </Flex>
      </Card>
    );
  },
);
